import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/usageTracker";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../lib/homePath";
import { ActionButton, SecondaryAction } from "../components/kit";
import PayChrome, { WithTokens, PlaceholderToken } from "../components/PayChrome";
import {
  CHARGED_TODAY,
  CHECKING,
  CHECK_AGAIN,
  FLOW_CURRENT,
  FLOW_RETURN,
  GO_TO_PLAYERS,
  GO_TO_RECORD,
  NEXT_CHARGE,
  NEXT_LABEL,
  NEXT_LINE,
  OPEN_SETTINGS_LINK,
  PERIOD_ANNUAL,
  PERIOD_MONTHLY,
  PLAN_NAME,
  RENEWS_NOTE_ANNUAL,
  RENEWS_NOTE_MONTHLY,
  RETURN_EYEBROW,
  RETURN_FOOT,
  RETURN_LEDE_CONFIRMING,
  RETURN_LEDE_FAILED,
  RETURN_LEDE_PAID,
  RETURN_ON_LABEL,
  RETURN_TITLE_CONFIRMING,
  RETURN_TITLE_FAILED,
  RETURN_TITLE_PAID,
  TOKEN,
  formatRenewal,
  priceFor,
  priceWithFee,
  RECEIPTS_LINE,
} from "../lib/payCopy";

/**
 * Back from Stripe (`/plan/return?session_id=`, #1036, D204.2), behind
 * `newUi("pay")`.
 *
 * THE PAGE PROVES NOTHING ITSELF. The address says only that Stripe sent the
 * person back. It asks `GET /api/billing/status?session_id=`, which has the
 * server retrieve the Checkout session and its subscription from Stripe and
 * write the row if the webhook has not (the F3 reconcile of the #1565 review),
 * and it claims a payment only when the row says `active`. While the answer is
 * `incomplete` it asks again every 2 seconds, for 20; after that it says it
 * could not confirm and that the person must not pay a second time.
 *
 * Faces: `confirming` (no gold), `active` (one gold: go to your record),
 * `annual`, `confirm-failed` (no gold; navy Check again). No toast (D109).
 */
const POLL_MS = 2000;
const POLL_LIMIT_MS = 20000;

const card = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: 14,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: 12,
};
const eyebrow = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--nw-ink-muted)",
};
const row = { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, fontSize: 14 };

export default function PaymentReturnPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id") || "";
  const { user } = useAuth();
  const nav = useNavigate();
  // "confirming" | "active" | "confirm-failed"
  const [phase, setPhase] = useState("confirming");
  const [row_, setRow] = useState(null);
  const [price, setPrice] = useState({ state: "loading", text: null });
  const mounted = useRef(true);
  const runId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadPrice = useCallback(async (status) => {
    try {
      const { data } = await api.get("/billing/plans");
      const hit = priceFor(data?.plans, status.plan_code, status.period);
      // D420: what was charged is the plan plus the card fee; a row without a fee shows no amount.
      const text = priceWithFee(hit)?.total ?? null;
      if (mounted.current) setPrice({ state: text ? "ready" : "none", text });
    } catch (e) {
      if (mounted.current) setPrice({ state: e?.response?.status === 503 ? "token" : "none", text: null });
    }
  }, []);

  const confirm = useCallback(async () => {
    const mine = ++runId.current;
    setPhase("confirming");
    const started = Date.now();
    const url = sessionId ? `/billing/status?session_id=${encodeURIComponent(sessionId)}` : "/billing/status";
    for (;;) {
      let status = null;
      try {
        const { data } = await api.get(url);
        status = data;
      } catch {
        status = null; // a failed ask is "not yet", until the 20 s are up
      }
      if (!mounted.current || mine !== runId.current) return;
      if (status?.status === "active") {
        setRow(status);
        track("plan.return_paid");
        setPhase("active");
        loadPrice(status);
        return;
      }
      // Anything the row says other than "waiting" is not a payment this page can claim.
      if (status && status.status !== "incomplete" && status.status !== "none") {
        track("plan.return_failed");
        setPhase("confirm-failed");
        return;
      }
      if (Date.now() - started >= POLL_LIMIT_MS) {
        track("plan.return_failed");
        setPhase("confirm-failed");
        return;
      }
      await new Promise((r) => setTimeout(r, POLL_MS));
      if (!mounted.current || mine !== runId.current) return;
    }
  }, [sessionId, loadPrice]);

  useEffect(() => {
    confirm();
    return () => {
      runId.current += 1;
    };
  }, [confirm]);

  const annual = row_?.period === "annual";
  const renewal = formatRenewal(row_?.current_period_end);
  const isCoach = user?.role === "coach";

  return (
    <PayChrome screenTestId="payment-return-new-screen" flow={FLOW_RETURN} current={FLOW_CURRENT.return} flowTestId="payment-return-flow">
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }} aria-live="polite">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {phase === "active" && <Check size={18} color="#1B5E20" strokeWidth={2.5} aria-hidden="true" />}
          <div className="nw-eyebrow">{RETURN_EYEBROW}</div>
        </div>
        <h1 className="nw-title-lg" style={{ margin: 0 }} data-testid="payment-return-title">
          {phase === "active" ? RETURN_TITLE_PAID : phase === "confirming" ? RETURN_TITLE_CONFIRMING : RETURN_TITLE_FAILED}
        </h1>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink-lede)" }} data-testid="payment-return-lede">
          {phase === "active" ? RETURN_LEDE_PAID : phase === "confirming" ? RETURN_LEDE_CONFIRMING : RETURN_LEDE_FAILED}
        </p>
      </div>

      {phase === "confirming" && (
        <div data-testid="payment-confirming" role="status" style={{ ...card, fontSize: 14, color: "var(--nw-ink-lede)" }}>
          {CHECKING}
        </div>
      )}

      {phase === "confirm-failed" && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="payment-confirm-failed">
            <SecondaryAction variant="navy-outline" onClick={confirm} data-testid="payment-check-again">
              {CHECK_AGAIN}
            </SecondaryAction>
            <Link to="/settings" data-testid="payment-check-settings" style={{ color: "var(--nw-navy)", fontSize: 14, textDecoration: "underline", minHeight: 44, display: "inline-flex", alignItems: "center" }}>
              {OPEN_SETTINGS_LINK}
            </Link>
          </div>
        </>
      )}

      {phase === "active" && (
        <>
          <div style={card} data-testid="payment-paid-for">
            <div style={eyebrow}>{RETURN_ON_LABEL}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={eyebrow}>
                <PlaceholderToken>{TOKEN.PLAN}</PlaceholderToken>
              </span>
              <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18, color: "var(--nw-ink)" }} data-testid="payment-plan-name">
                {PLAN_NAME[row_?.plan_code] || ""}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--nw-ink-muted)" }} data-testid="payment-period">
                {annual ? PERIOD_ANNUAL : PERIOD_MONTHLY}
              </span>
            </div>
            {price.state !== "none" && price.state !== "loading" && (
              <div style={row} data-testid="payment-charged">
                <span style={{ color: "var(--nw-ink-muted)" }}>{CHARGED_TODAY}</span>
                <strong>{price.state === "ready" ? price.text : <PlaceholderToken>{TOKEN.PRICE}</PlaceholderToken>}</strong>
              </div>
            )}
            <div style={row} data-testid="payment-next">
              <span style={{ color: "var(--nw-ink-muted)" }}>{NEXT_CHARGE}</span>
              <strong>{renewal || <PlaceholderToken>{TOKEN.NEXT_PAYMENT_DATE}</PlaceholderToken>}</strong>
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }} data-testid="payment-renews-note">
              {annual ? RENEWS_NOTE_ANNUAL : RENEWS_NOTE_MONTHLY}
            </div>
          </div>

          <div style={card} data-testid="payment-next-steps">
            <div style={eyebrow}>{NEXT_LABEL}</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{NEXT_LINE}</div>
            <div data-testid="payment-receipts" style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
              {RECEIPTS_LINE}
            </div>
          </div>

          <ActionButton data-testid="payment-continue" onClick={() => nav(homePathForRole(user?.role))}>
            {isCoach ? GO_TO_PLAYERS : GO_TO_RECORD}
          </ActionButton>

          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
            <WithTokens text={RETURN_FOOT} />
          </div>
        </>
      )}
    </PayChrome>
  );
}
